/**
 * Copyright (c) 2024 rports (r), All rights reserved.
 *
 *     ______    _______  _______  ______    _______  _______ 
 *    |    _ |  |       ||       ||    _ |  |       ||       |
 *    |   | ||  |    _  ||   _   ||   | ||  |_     _||  _____|
 *    |   |_||_ |   |_| ||  | |  ||   |_||_   |   |  | |_____ 
 *    |    __  ||    ___||  |_|  ||    __  |  |   |  |_____  |
 *    |   |  | ||   |    |       ||   |  | |  |   |   _____| |
 *    |___|  |_||___|    |_______||___|  |_|  |___|  |_______|
 *
 * filename      : Layout.jsx
 * description   : {{insert here}}
 * creation date : 31/01/2024
 * author        : "Luis CRUZ" <luis.cruz@rports.net>
 */



// --------------------------------------------------------------------------------
//      IMPORTS
// --------------------------------------------------------------------------------

import {
    useEffect,
    useState,
    Fragment,
} from 'react';

import {
    Outlet,
} from 'react-router-dom';

import {
    View,
} from '@aws-amplify/ui-react';

import {
    useComponentContext,
    toolKit,
} from '@/components/core';

import {
    id_list,
} from '@/utils/router/core';

import {
    brand_logo_color,
} from '@/components/snippets/BrandLogo';

import {
    config_page_LIST_navbar,
} from '@/components/layouts/main/core';

import {
    config_page_LIST_footer_contents,
    config_page_LIST_footer_legal,
    config_page_LIST_footer_demo,
} from '@/components/layouts/main/core';

import NavBar from '@/components/snippets/NavBar';
import Footer from '@/components/snippets/Footer';



// --------------------------------------------------------------------------------
//      FUNCTIONS
// --------------------------------------------------------------------------------

function Layout() {
    // Component context
    const { ctx } = useComponentContext();

    // State variables
    const [currentAuthState, set_currentAuthState] = useState(undefined);
    const [currentPath, set_currentPath] = useState(undefined);
    const [currentAnchor, set_currentAnchor] = useState(undefined);
    const [minHeight, set_minHeight] = useState(undefined);

    // Footer configuration pages
    const footer_configPages =
        [
            {
                label: 'Contents', // TODO: Translate message
                dict: config_page_LIST_footer_contents,
            },
            {
                label: 'Legal mentions', // TODO: Translate message
                dict: config_page_LIST_footer_legal,
            },
            {
                label: 'Demonstration', // TODO: Translate message
                dict: config_page_LIST_footer_demo,
            },
        ];

    // Listen asynchronous events: Page load, location change and minimum height
    useEffect(() => {
        //
        // Authentication change detection
        //
        if (
            (ctx.runtime.isLoaded) &&
            (ctx.auth.state !== toolKit.enums.auth.stateTypes.UNMOUNTED)
        ) {
            if (ctx.auth.state !== currentAuthState) {
                // Update the state
                set_currentAuthState(ctx.auth.state);
                set_minHeight(undefined);
            }
        }

        //
        // Location change detection
        //
        if (
            (ctx.runtime.isLoaded) &&
            (ctx.auth.state !== toolKit.enums.auth.stateTypes.UNMOUNTED) &&
            (undefined !== minHeight)
        ) {
            const path = ctx.api.location.getPath();
            const hash = toolKit.api.location.serializeHash(ctx.api.location.getHash());

            if ((path !== currentPath) || (hash !== currentAnchor)) {
                // Update the state
                set_currentPath(path);
                set_currentAnchor(hash);

                // Prepare the arguments
                const reference = toolKit.api.location.serializeHash([id_list.NAVBAR]);
                const target = Boolean(hash) ? hash : reference;
                var scrollBehavior = 'smooth';

                // Detect any page change
                if (path !== currentPath) {
                    scrollBehavior = 'instant';
                }

                // Scroll to the target
                toolKit.api.window.scrollTo(target, reference, scrollBehavior);
            }
        }

        //
        // Minimum height computation
        //
        const ref_navbar = toolKit.api.location.serializeHash([id_list.NAVBAR]);
        const ref_footer = toolKit.api.location.serializeHash([id_list.FOOTER]);

        if (ref_navbar && ref_footer) {
            const obj_navbar = toolKit.api.utils.getBounding(ref_navbar);
            const obj_footer = toolKit.api.utils.getBounding(ref_footer);

            // Compute the minimum height
            const val = Math.max(0, (ctx.runtime.screen.inner.height - obj_navbar.height - obj_footer.height));

            // Set the minimum height
            set_minHeight(val);
        }
    },
        [
            ctx,
            currentAuthState,
            currentPath,
            currentAnchor,
            minHeight,
        ]
    );

    return (
        <Fragment>
            <View
                className='layout-main-background'
            >
                <View>
                    <NavBar
                        classPrefix='navbar-main'
                        configPages={config_page_LIST_navbar}
                        logoColor={brand_logo_color.light}
                    />

                    <View
                        minHeight={minHeight}
                    >
                        <Outlet />
                    </View>
                </View>

                <Footer
                    classPrefix='footer-main'
                    configPages={footer_configPages}
                />
            </View>
        </Fragment>
    );
}



// --------------------------------------------------------------------------------
//      EXPORTED OBJECTS
// --------------------------------------------------------------------------------

export default Layout;



// End of file
